import { Button } from '@foam/buoy/src/components/Button/Button';
import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { memo, useCallback, useMemo } from 'react';
import { Tooltip } from 'ui-components';
import { ContentCardPlatformIcon } from '~/components/ContentCard/components';
import { useGetContent } from '~/hooks/api/useGetContent';
import { useGetAuthToken } from '~/hooks/useGetAuthToken';
import { ISearchContentResult } from '~/searchApi';
import { formatAbbr } from '~/utils';

import { getMetricsToShow } from '../utils';

interface CreatorInfoSectionProps {
  content: ISearchContentResult;
  showMetrics: boolean;
  onClick?: (content: ISearchContentResult) => void;
}

const CreatorInfoSectionComponent = ({
  content,
  showMetrics,
  onClick,
}: CreatorInfoSectionProps) => {
  // Get auth token for shared pages
  const authToken = useGetAuthToken({
    creatorSlug: content.creator.slug,
  });

  // Fetch individual content metrics only when showMetrics is true
  const { data: contentResponse } = useGetContent(
    content.id,
    authToken as string,
  );

  // Use fetched content with metrics if available, otherwise use the original content
  const contentToUse = useMemo(() => {
    if (!showMetrics || !contentResponse?.data?.[0]) {
      return content;
    }

    // Merge the fetched metrics with the original content
    return {
      ...content,
      ...contentResponse.data[0],
    } as ISearchContentResult;
  }, [content, contentResponse, showMetrics]);

  const metricsToShow = getMetricsToShow(contentToUse);

  return (
    <div className="flex flex-1 shrink-0 flex-col justify-between gap-3 px-3 pb-3 pt-2">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 align-middle">
          <ContentCardPlatformIcon
            width={6}
            height={6}
            platform={content.platform}
          />
          <span className="w-[50px] flex-1 truncate text-16 font-500 text-[var(--media-kit-text-base)]">
            <Tooltip label={content.creator.fullName} placement="top-start">
              <span>{content.creator.fullName}</span>
            </Tooltip>
          </span>
        </div>

        {/* Metrics Row */}
        {showMetrics ? (
          <div className="flex flex-wrap items-center gap-2 text-14 text-[var(--media-kit-text-subtle)]">
            {metricsToShow.showView && (
              <Tooltip label="Views" placement="top">
                <div className="flex items-center gap-1">
                  <Icon name="Eye" className="size-4" />
                  <span>{formatAbbr(metricsToShow.viewValue)}</span>
                </div>
              </Tooltip>
            )}
            {metricsToShow.showReach && (
              <Tooltip label="Reach" placement="top">
                <div className="flex items-center gap-1">
                  <Icon name="Reach" className="size-4" />
                  <span>{formatAbbr(metricsToShow.reachValue)}</span>
                </div>
              </Tooltip>
            )}
            {metricsToShow.showEngagements && (
              <Tooltip label="Engagements" placement="top">
                <div className="flex items-center gap-1">
                  <Icon name="Click" className="size-4" />
                  <span>{formatAbbr(metricsToShow.engagementsValue)}</span>
                </div>
              </Tooltip>
            )}
            {metricsToShow.showLikes && (
              <Tooltip label="Likes" placement="top">
                <div className="flex items-center gap-1">
                  <Icon name="ThumbUp" className="size-4" />
                  <span>{formatAbbr(metricsToShow.likesValue)}</span>
                </div>
              </Tooltip>
            )}
            {metricsToShow.showComments && (
              <Tooltip label="Comments" placement="top">
                <div className="flex items-center gap-1">
                  <Icon name="Chat" className="size-4" />
                  <span>{formatAbbr(metricsToShow.commentsValue)}</span>
                </div>
              </Tooltip>
            )}
          </div>
        ) : null}
      </div>

      {/* View Details Button */}
      {showMetrics ? (
        <ViewDetailsButton contentToUse={contentToUse} onClick={onClick} />
      ) : null}
    </div>
  );
};

interface ViewDetailsButtonProps {
  contentToUse: ISearchContentResult;
  onClick?: (content: ISearchContentResult) => void;
}

const ViewDetailsButton = memo(
  ({ contentToUse, onClick }: ViewDetailsButtonProps) => {
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(contentToUse);
      },
      [onClick, contentToUse],
    );

    return (
      <Button
        onClick={handleClick}
        variant="tertiary"
        size="md"
        className="justify-self-end"
      >
        <Icon name="Stats" />
        View details
      </Button>
    );
  },
);

export const CreatorInfoSection = memo(CreatorInfoSectionComponent);
